import { compose, lifecycle, withProps, withState, withHandlers, withStateHandlers } from 'recompose'
import { show } from 'redux-modal'
import { connect } from 'react-redux'
import { isEqual } from 'lodash'
import isEmpty from 'lodash/isEmpty'
import withType from 'components/common/hoc/withType'
import { TPP_MODAL } from 'constant'
import { getQuoteItemAndServices, getFreeAligmentTooltip } from 'selectors/shoppingData'
import { getCurrentStoreNumber } from 'selectors/getCurrentStore'
import { getLocationPathname } from 'selectors/getPageLocation'
import { googleAnalyticCheckout } from 'actions/googleAnalytic'
import { navigateToProperQuotePage } from 'actions/quote'
import { showChangedAvailabilityPriceModal } from 'components/ChangedAvailabilityPriceModal/actions'
import { setQuoteProductAndServices } from '../../QuickAppointmentPage/actions'
import { getCartDetail, getCitiFinancingPromoExcludedStores } from '../actions'
import { addProductsWithChangedAvailabilityOrPrice } from 'components/common/hoc/withAddToQuote/actions.js'
import { isQuoteSummaryPage } from 'helpers/quoteRelatedPages'
import {
  getFreeAlignmentCheckBlockState,
  getItemWithFilteredServices,
  getProductsWithChangedPrice,
  sortProducts,
  injectDynamicVerbiage,
  getSkusWithChangedAvailability,
  getSkusWithChangedAvailabilityOrPrice
} from './helpers'
import { SIX_MONTHS } from './constants'
import BOTProductAndServicesQuoteNew from '../BOTProductAndServicesQuoteNew'

const mapStateToProps = state => {
  const { app: { isFetching, isLoading, isNavigating } = {}} = state
  return {
    quotedItemAndServices: getQuoteItemAndServices(state),
    freeAligmentTooltip: getFreeAligmentTooltip(state),
    pathname: getLocationPathname(state),
    avoidNavigation: isFetching || isLoading || isNavigating,
    storeNumber: getCurrentStoreNumber(state),
    skusAddedToCart: state.addToQuote.skusAddedToCart,
    inventoryStatusLoaded: state.updatingBlocks.inventoryStatusLoaded,
    shoppingQuoteTotalQuantity: state.updatingBlocks.shoppingQuoteTotalQuantity,
    productsWithChangedAvailability: state.addToQuote.productsWithChangedAvailability
  }
}

const withShowTPPBannerState = withState('showTPPBanner', 'setShowTPPBanner', true)

const withImprovedProps = withProps(({
  quotedItemAndServices: {
    products: quoteProducts = [],
    services = []
  },
  quotedItemAndServices,
  freeAligmentTooltip,
  products
}) => ({
  cartIsEmpty: !quoteProducts.length && !services.length,
  freeAlignmentCheckDisabled: getFreeAlignmentCheckBlockState({ quotedItemAndServices, freeAligmentTooltip }),
  quotedItemAndServices: getItemWithFilteredServices({ quotedItemAndServices, freeAligmentTooltip }),
  productsWithChangedPrice: getProductsWithChangedPrice(quotedItemAndServices),
  products: sortProducts(products)
}))

const withQuoteHandlers = withHandlers({
  initQuote: ({
    getCartDetail,
    googleAnalyticCheckout
  }) => () =>
    getCartDetail()
      .then(() => {
        googleAnalyticCheckout()
      }) // google analytic checkout
})

const withTPPHandlers = withHandlers({
  handleShowTPPModal: ({ show }) => () => show(TPP_MODAL),
  handleCloseTPPBanner: ({ setShowTPPBanner }) => () => setShowTPPBanner(false)
})

const withPaymentOptionsBannerState = withStateHandlers({
  paymentOptionsBannerContent: null,
  citiFinancingPromoExcludedStores: {}
},
{
  setPaymentOptionsBannerContent: () => paymentOptionsBannerContent => ({ paymentOptionsBannerContent }),
  setCitiFinancingPromoExcludedStores: () => citiFinancingPromoExcludedStores => ({ citiFinancingPromoExcludedStores })
})

const withPaymentOptionsBannerHandlers = withHandlers({
  evaluateEstimatedTotal: ({
    storeNumber,
    setPaymentOptionsBannerContent,
    paymentOptionsBannerTemplate = ''
  }) => ({
    estimatedTotal,
    citiFinancingPromoExcludedStores: {
      sixMonths = ''
    } = {}
  }) =>
    setTimeout(() => {
      const paymentOption = estimatedTotal > 249.99 ? SIX_MONTHS : ''
      const threePaymentsValue = (estimatedTotal / 3).toFixed(2)
      const monthlyPaymentAmount = (estimatedTotal / Number(paymentOption)).toFixed(2)
      const promoIsStoreEnabled = paymentOption === SIX_MONTHS
        ? !sixMonths.includes(storeNumber)
        : true

      paymentOption && promoIsStoreEnabled && paymentOptionsBannerTemplate ?
        setPaymentOptionsBannerContent(injectDynamicVerbiage({
          paymentOptionsBannerTemplate,
          estimatedTotal,
          monthlyPaymentAmount,
          paymentOption,
          threePaymentsValue
        })) : setPaymentOptionsBannerContent('')
    })
})

const withInit = lifecycle({
  componentDidMount() {
    const {
      setQuoteProductAndServices,
      getCitiFinancingPromoExcludedStores,
      setCitiFinancingPromoExcludedStores,
      quotedItemAndServices: { products, services }
    } = this.props

    setQuoteProductAndServices({ products, services })
    getCitiFinancingPromoExcludedStores()
      .then(({ contentDetails = {}} = {}) => {
        const citiFinancingPromoExcludedStores = {
          sixMonths: contentDetails.six_months_financing_promo_excluded_stores
        }
        setCitiFinancingPromoExcludedStores(citiFinancingPromoExcludedStores)
      })
  },
  componentDidUpdate({
    storeNumber: prevStoreNumber,
    quotedItemAndServices: { quoteSummary: { estimatedTotal: prevEstimatedTotal } = {}} = {},
    citiFinancingPromoExcludedStores: prevCitiFinancingPromoExcludedStores
  }) {
    const {
      pathname,
      navigateToProperQuotePage,
      avoidNavigation,
      storeNumber,
      citiFinancingPromoExcludedStores,
      evaluateEstimatedTotal,
      quotedItemAndServices: {
        quoteSummary: { estimatedTotal } = {}
      } = {}
    } = this.props

    if (prevStoreNumber !== storeNumber || prevEstimatedTotal !== estimatedTotal ||
      !isEqual(prevCitiFinancingPromoExcludedStores, citiFinancingPromoExcludedStores)) {
      evaluateEstimatedTotal({ estimatedTotal, citiFinancingPromoExcludedStores })
    }
    !avoidNavigation !== isQuoteSummaryPage(pathname) &&
      navigateToProperQuotePage()
  },
  UNSAFE_componentWillReceiveProps({
    setQuoteProductAndServices,
    quotedItemAndServices: { products, services },
    productsWithChangedPrice,
    skusAddedToCart,
    productsWithChangedAvailability,
    modalState
  }) {
    const skusWithChangedAvailability = []
    let productsWithChangedAvailabilityOrPrice = []

    setQuoteProductAndServices({ products, services })

    if (!isEqual(this.props.skusAddedToCart, skusAddedToCart) &&
      isEmpty(this.props.inventoryStatusLoaded) &&
      !this.props.shoppingQuoteTotalQuantity &&
      isQuoteSummaryPage(this.props.pathname)
    ) {
      const skusWithPreviousAvailability = JSON.parse(localStorage.getItem('skusWithPreviousAvailability')) || []
      if (skusWithPreviousAvailability.length >= skusAddedToCart.length) {
        productsWithChangedAvailabilityOrPrice = getSkusWithChangedAvailability(
          skusWithPreviousAvailability,
          skusAddedToCart,
          this.props.quotedItemAndServices,
          skusWithChangedAvailability
        )
        // To get the products with changed prices.
        if (productsWithChangedPrice.length) {
          productsWithChangedAvailabilityOrPrice = getSkusWithChangedAvailabilityOrPrice(
            productsWithChangedPrice,
            productsWithChangedAvailabilityOrPrice
          )
        }
      }
      this.props.addProductsWithChangedAvailabilityOrPrice(productsWithChangedAvailabilityOrPrice)
    }
    // To call the availability/price change modal
    if (!this.props.productsWithChangedAvailability?.length && productsWithChangedAvailability?.length) {
      this.props.showChangedAvailabilityPriceModal(productsWithChangedAvailability)
    }
  }
})

export default compose(
  withType({ type: 'BOTProductAndServicesQuote' }),
  connect(mapStateToProps, {
    getCartDetail,
    setQuoteProductAndServices,
    showChangedAvailabilityPriceModal,
    googleAnalyticCheckout,
    navigateToProperQuotePage,
    getCitiFinancingPromoExcludedStores,
    show,
    addProductsWithChangedAvailabilityOrPrice
  }),
  withShowTPPBannerState,
  withImprovedProps,
  withQuoteHandlers,
  withTPPHandlers,
  withPaymentOptionsBannerState,
  withPaymentOptionsBannerHandlers,
  withInit
)(BOTProductAndServicesQuoteNew)
